<template>
    <div class="my-blocks">
        <div class="blocks-list">
            <div class="block-item" v-for="block in blocks" :key="block._id">
                <div class="block-info">
                    <span class="material-icons block-icon">{{ block.icon || "extension" }}</span>
                    <div class="block-details">
                        <h3>{{ block.name }}</h3>
                        <p>{{ block.description }}</p>
                        <div class="block-status">
                            <a-tag :color="getStatusColor(block.approvalStatus)">
                                {{ getStatusText(block.approvalStatus) }}
                            </a-tag>
                            <a-tag v-if="block.publishedVersion" color="blue">
                                {{ $t("chatbotv2.published") || "Published" }}
                            </a-tag>
                            <a-tag v-else color="orange">
                                {{ $t("chatbotv2.draft") || "Draft" }}
                            </a-tag>
                            <span class="stats">
                                <a-icon type="download" /> {{ block.usageCount || 0 }}
                                <a-icon type="star" style="margin-left: 12px" />
                                {{ block.averageRating ? block.averageRating.toFixed(1) : "0.0" }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="block-actions">
                    <a-button size="small" @click="editBlock(block._id)">
                        <a-icon type="edit" />
                        {{ $t("message.edit") || "Edit" }}
                    </a-button>
                    <a-button
                        v-if="!block.publishedVersion"
                        size="small"
                        type="primary"
                        @click="publishBlock(block._id)"
                    >
                        <a-icon type="rocket" />
                        {{ $t("chatbotv2.publish") || "Publish" }}
                    </a-button>
                    <a-button
                        v-if="block.visibility !== 'unlisted' && block.publishedVersion"
                        size="small"
                        @click="unlistBlock(block._id)"
                    >
                        <a-icon type="eye-invisible" />
                        {{ $t("chatbotv2.unlist") || "Unlist" }}
                    </a-button>
                    <a-popconfirm
                        v-if="!block.publishedVersion || (block.usageCount || 0) === 0"
                        :title="$t('chatbotv2.confirmDeleteBlock') || 'Are you sure you want to delete this block?'"
                        @confirm="deleteBlock(block._id)"
                    >
                        <a-button size="small" type="danger">
                            <a-icon type="delete" />
                        </a-button>
                    </a-popconfirm>
                    <a-tooltip
                        v-else-if="block.publishedVersion && (block.usageCount || 0) > 0"
                        :title="$t('chatbotv2.cannotDeleteActiveBlock') || 'Cannot delete block with active usage'"
                    >
                        <a-button size="small" type="danger" disabled>
                            <a-icon type="delete" />
                        </a-button>
                    </a-tooltip>
                </div>
            </div>
        </div>

        <div class="empty-state" v-if="blocks.length === 0">
            <a-empty :description="$t('chatbotv2.noBlocksCreated') || 'No blocks created yet'" />
        </div>

        <!-- Changelog Modal -->
        <Modal2
            v-if="showChangelogModal"
            :title="$t('chatbotv2.publishBlock') || 'Publish Block'"
            :description="$t('chatbotv2.enterChangelog') || 'Enter changelog (optional)'"
            :okText="$t('chatbotv2.publish') || 'Publish'"
            :cancelText="$t('message.cancel') || 'Cancel'"
            @ok="confirmPublish"
            @cancel="showChangelogModal = false"
        >
            <a-textarea
                v-model="changelogInput"
                :rows="5"
                :placeholder="$t('chatbotv2.changelogPlaceholder') || 'Describe the changes made in this version...'"
            />
        </Modal2>
    </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import Modal2 from "../../../../../components/common/Modal2.vue";

export default {
    name: "MyBlocks",
    components: {
        Modal2,
    },
    data() {
        return {
            showChangelogModal: false,
            changelogInput: "",
            selectedBlockId: null,
        };
    },
    computed: {
        ...mapState({
            blocks: (state) => state.customBlocks.myBlocks,
        }),
    },
    methods: {
        ...mapActions({
            listMyBlocks: "customBlocks/listMyBlocks",
            publishBlockAction: "customBlocks/publishBlock",
            unlistBlockAction: "customBlocks/unlistBlock",
            deleteBlockAction: "customBlocks/deleteBlock",
        }),
        editBlock(blockId) {
            this.$emit("edit-block", blockId);
        },
        publishBlock(blockId) {
            this.selectedBlockId = blockId;
            this.changelogInput = "";
            this.showChangelogModal = true;
        },
        async confirmPublish() {
            try {
                this.showChangelogModal = false;
                await this.publishBlockAction({ id: this.selectedBlockId, changelog: this.changelogInput || "" });
                this.$message.success(this.$t("chatbotv2.blockPublished") || "Block published successfully");
                await this.listMyBlocks();
            } catch (error) {
                this.$message.error(error.message || "Failed to publish block");
            }
        },
        async unlistBlock(blockId) {
            try {
                await this.unlistBlockAction(blockId);
                this.$message.success(this.$t("chatbotv2.blockUnlisted") || "Block unlisted successfully");
                await this.listMyBlocks();
            } catch (error) {
                this.$message.error(error.message || "Failed to unlist block");
            }
        },
        async deleteBlock(blockId) {
            try {
                await this.deleteBlockAction(blockId);
                this.$message.success(this.$t("chatbotv2.blockDeleted") || "Block deleted successfully");
            } catch (error) {
                this.$message.error(error.message || "Failed to delete block");
            }
        },
        getStatusColor(status) {
            const colors = {
                pending: "orange",
                approved: "green",
                rejected: "red",
            };
            return colors[status] || "default";
        },
        getStatusText(status) {
            const texts = {
                pending: this.$t("chatbotv2.pending") || "Pending",
                approved: this.$t("chatbotv2.approved") || "Approved",
                rejected: this.$t("chatbotv2.rejected") || "Rejected",
            };
            return texts[status] || status;
        },
    },
};
</script>

<style lang="scss" scoped>
.my-blocks {
    .blocks-list {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .block-item {
            background: #fff;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            padding: 20px;
            display: flex;
            justify-content: space-between;
            align-items: center;

            .block-info {
                display: flex;
                gap: 16px;
                flex: 1;

                .block-icon {
                    font-size: 48px;
                    color: #9c27b0;
                }

                .block-details {
                    flex: 1;

                    h3 {
                        margin: 0 0 8px 0;
                        font-size: 18px;
                    }

                    p {
                        margin: 0 0 12px 0;
                        color: #666;
                        font-size: 14px;
                    }

                    .block-status {
                        display: flex;
                        gap: 8px;
                        align-items: center;

                        .stats {
                            margin-left: 12px;
                            color: #999;
                            font-size: 14px;
                        }
                    }
                }
            }

            .block-actions {
                display: flex;
                gap: 8px;
            }
        }
    }

    .empty-state {
        min-height: 400px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
}
</style>

